import Head from '@/components/Head';
import { PageHeadKeys } from '@gik/core/hocs/PageHeadKeys';
import he from 'he';
import React from 'react';
import { stripHtml } from 'string-strip-html';
import type { TitleWithFallback } from './withTitle';
import { generateTitle } from './withTitle';

type ImageDimensions = {
  width: number;
  height: number;
};

type OGTypes = 'product' | 'article'; // add new types here as needed

type MetaTags = {
  title?: string | TitleWithFallback;
  thumbnailTitle?: string;
  description: string;
  imageUrl?: string;
  imageDimensions?: ImageDimensions;
  keywords?: string;
  type?: OGTypes;
  noIndex?: boolean;
};

// TODO: Move JSON-LD logic here somewhere too to consistently use the same values and have all the pages also
//  effortlessly have those tags as well

export function createMetaTags(meta: MetaTags) {
  const titleWithSuffix = generateTitle(meta.title);
  const title = generateTitle(meta.title, false);
  const description = meta.description && stripHtml(he.decode(meta.description)).result;
  const ogTitle = meta.thumbnailTitle ?? title;

  const tags = [
    <title key={PageHeadKeys.TITLE}>{titleWithSuffix}</title>,
    <meta property="og:title" content={ogTitle} key={PageHeadKeys.META_OG_TITLE} />,
    <meta name="twitter:title" content={ogTitle} key={PageHeadKeys.META_TWITTER_TITLE} />,
    ...(description
      ? [
          <meta name="description" content={description} key={PageHeadKeys.META_DESCRIPTION} />,
          <meta property="og:description" content={description} key={PageHeadKeys.META_OG_DESCRIPTION} />,
          <meta name="twitter:description" content={description} key={PageHeadKeys.META_TWITTER_DESCRIPTION} />,
        ]
      : []),
    ...(meta.imageUrl
      ? [
          <meta property="og:image" content={meta.imageUrl} key={PageHeadKeys.META_OG_IMAGE} />,
          <meta property="og:image:secure_url" content={meta.imageUrl} key={PageHeadKeys.META_OG_IMAGE_SECURE_URL} />,
          <meta property="twitter:image" content={meta.imageUrl} key={PageHeadKeys.META_TWITTER_IMAGE} />,
        ]
      : []),
    <meta
      property="og:image:width"
      content={meta.imageUrl && meta.imageDimensions?.width ? meta.imageDimensions.width.toString() : null}
      key={PageHeadKeys.META_OG_IMAGE_WIDTH}
    />,
    <meta
      property="og:image:height"
      content={meta.imageUrl && meta.imageDimensions?.height ? meta.imageDimensions.height.toString() : null}
      key={PageHeadKeys.META_OG_IMAGE_HEIGHT}
    />,
    ...(meta.keywords ? [<meta name="keywords" key={PageHeadKeys.META_KEYWORDS} content={meta.keywords} />] : []),
    ...(meta.type ? [<meta property="og:type" key={PageHeadKeys.META_OG_TYPE} content={meta.type} />] : []),
    ...(meta.noIndex ? [<meta name="robots" key={PageHeadKeys.META_ROBOTS} content="noindex" />] : []),
  ];

  return tags;
}

export function MetaTagsAssembler({ tags, children }: React.PropsWithChildren<{ tags: JSX.Element[] }>) {
  if (tags?.length === 0) {
    return <>{children}</>;
  } else {
    return (
      <>
        <Head>{tags}</Head>
        {children}
      </>
    );
  }
}

export function withMeta(meta: MetaTags, component: React.ReactNode) {
  return <MetaTagsAssembler tags={createMetaTags(meta)}>{component}</MetaTagsAssembler>;
}
